import { Component, ElementRef, Input, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation, afterRender } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';

import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';

import { CerFormView } from '@form/cer-form-view';
import { ICerFormViewType } from '@form/cer-form-view-type/cer-form-view-type.interface';
import { uiCmdDocumentDownload, uiCmdDocumentDownloadAsPdf, uiCmdDocumentEdit, uiCmdDocumentEditCancel, uiCmdDocumentOpenLink, uiCmdDocumentSave } from '@cer/cer-ui/cer-ui-commands';

import { CerLink } from 'src/cer/cer-markdown/cer-link';
import { CerMarkdownService } from 'src/cer/cer-markdown/cer-markdown.service';

import { CerToolbarItemState } from '@cer/cer-toolbar/cer-toolbar-command-adapter';
import { CerMarkdownEdit } from '@cer/cer-markdown/cer-markdown-edit';
import { CerFormToolbar } from '@cer/cer-form/cer-form-toolbar';
import { CerLinkedAttachmentUploadProgress, CerLinkedAttachmentUploader } from '@cer/cer-data/cer-linked-attachment/cer-linked-attachment-uploader';
import { CerPromptService } from '@cer/cer-ui/cer-ui-prompt.service';
import { CerRandom } from '@cer/cer-util/cer-random';
import { documentToolbarItemsStateGet, iconImgSrcSource } from './cer-form-view-document-type';
import { CerMarkdownPdf } from '@cer/cer-markdown/cer-markdown-pdf';

@Component({
  selector: 'cer-form-view-type-document-markdown',
  template: `
    <div *ngIf="hasToolbar" [id]='id' class="cer-flex-static cer-overflow-hidden" #toolbarDiv></div>
    <div #markdownViewDiv *ngIf="!isEdit" class="cer-flex-main cer-overflow-auto cer-padding-left-8 markdown-body" [innerHTML]="html"></div>
    <div #markdownEditDiv *ngIf="isEdit" class="cer-flex-main cer-overflow-auto markdown-body"></div>
    `,
  encapsulation: ViewEncapsulation.None,
  styleUrls: [
    '../../../../../node_modules/@milkdown/crepe/src/theme/common/style.css' // Markdown Editor styles
  ],
  providers: [
    CerMarkdownService
  ]
})

export class CerFormViewTypeDocumentMarkdownComponent implements ICerFormViewType, OnDestroy {

  constructor(private markdownSvc: CerMarkdownService, private httpClient: HttpClient, private ui: CerPromptService) {

    afterRender(() => { this.refreshElement(); });

    this.markdownSvc.linksClicked$.subscribe(links => this.triggerLinks(links));
  }

  private subscriptionManager: Subscription = new Subscription();
  public ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
    this.markdownSvc.linksClicked$.unsubscribe();
    this.toolbar?.destroy();
  }

  @ViewChild(TemplateRef<any>) template!: TemplateRef<any>;

  private _view!: CerFormView;
  get view(): CerFormView { return this._view; };
  @Input() set view(view: CerFormView) {
    if (view && this._view != view) {
      this._view = view;
      this.subscriptionManager.add(
        this._view.commandSubscribe(e => this.onCommand(e))
      );
      this.id = view.id + '_md';
      if (view.document?.length > 0) {
        this.getDocument();
        this.documentUrl = null;
        this.documentUrlUpdate = null;
      }
      else {
        this.documentUrl = this._view.documentUrl;
        this.documentUrlUpdate = this._view.documentUrlUpdate;
      }
      this.allowEdit = view?.viewMetadata?.allowEdit === true && this.documentUrlUpdate?.length > 0;
    }
  }

  @Input() id: string = CerRandom.AsString(10);

  private _documentUrl!: string;
  get documentUrl(): string { return this._documentUrl };
  @Input() set documentUrl(documentUrl: string) {
    if (documentUrl != this._documentUrl) {
      this._documentUrl = documentUrl;
      this.editSet(false);
      this.getDocument();
    }
  }

  private _documentUrlUpdate!: string;
  get documentUrlUpdate(): string { return this._documentUrlUpdate };
  @Input() set documentUrlUpdate(documentUrlUpdate: string) {
    if (documentUrlUpdate != this._documentUrlUpdate) {
      this._documentUrlUpdate = documentUrlUpdate;
      this.allowEdit = this.view?.viewMetadata?.allowEdit === true && this.documentUrlUpdate?.length > 0;
    }
  }

  // #region Toolbar
  protected hasToolbar: boolean = true;
  private toolbar: CerFormToolbar;
  @ViewChild('toolbarDiv') set toolbarDivRefSet(ele: ElementRef<HTMLDivElement>) {
    this.toolbarInit(ele?.nativeElement);
  }

  private toolbarInit(divParent: HTMLDivElement) {
    if (divParent != null && this.toolbar == null) {
      this.toolbar = new CerFormToolbar(divParent, this._view, [uiCmdDocumentEdit, uiCmdDocumentSave, uiCmdDocumentEditCancel, uiCmdDocumentDownloadAsPdf], this.toolbarItemsStateGet.bind(this));
      this.hasToolbar = this.toolbar != null;
    }
  }

  private toolbarItemsStateGet(): Map<string, CerToolbarItemState> {

    const downloadUrl = this.isEdit ? null : this.documentUrl;
    const canEdit = (this.allowEdit && !this.isEdit);
    
    const states = documentToolbarItemsStateGet(false, false, downloadUrl, null, null);
    
    states.set(uiCmdDocumentDownload.id, { visible: canEdit, align: 'right' });
    states.set(uiCmdDocumentOpenLink.id, { visible: canEdit, align: 'right' });
    states.set(uiCmdDocumentDownloadAsPdf.id, { visible: !this.isEdit, iconSrc: iconImgSrcSource('pdf'), align: 'right' });
  
    states.set(uiCmdDocumentEdit.id, { visible: canEdit });
    states.set(uiCmdDocumentSave.id, { visible: this.isEdit });
    states.set(uiCmdDocumentEditCancel.id, { visible: this.isEdit });
  
    return states;
  }

  // #endregion

  protected isEdit: boolean = false;
  private editSet(setValue: boolean = null) {
    this.isEdit = setValue ?? !this.isEdit;
    this.toolbar?.itemStateApply();
  }

  public onCommand(e: UiCommandEvent) {
    switch (e.commandId) {
      case uiCmdDocumentEdit.id:
        this.editSet(true);
        break;
      case uiCmdDocumentSave.id:
        this.documentEditSaveAndClose();
        break;
      case uiCmdDocumentEditCancel.id:
        this.documentEditClose();
        break;
      case uiCmdDocumentDownloadAsPdf.id:
        this.downloadAsPdf();
        break;
    }
  }

  // #region Markdown view
  protected html: string;
  protected markdown: string;

  private markdownViewDivEle: HTMLDivElement = null;
  @ViewChild('markdownViewDiv') set markdownViewDivSet(e: ElementRef) {
    const ele: HTMLDivElement = e?.nativeElement;
    if (ele != null && ele != this.markdownViewDivEle) {
      this.markdownViewDivEle = e.nativeElement as HTMLDivElement;
      this.refreshElement();
    }
  }

  private refreshElement() {
    if (this.markdownViewDivEle != null) {
      this.markdownSvc.refreshElement(this.markdownViewDivEle);
    }
  }

  private markdownParse(md: string) {
    this.markdown = md ?? "";
    this.html = this.markdownSvc.parse(md);
    this.refreshElement();
  }
  //#endregion

  //#region Link click
  private triggerLinks(links: CerLink[]): void {
    links?.forEach(link => {
      this.triggerLink(link);
    });
  }

  public triggerLink(link: CerLink) {
    const e: UiCommandEvent = { source: UiCommandSourceEnum.Event, args: link, commandId: null, actionType: UiActionTypeEnum.LinkClicked, subAction: null };
    this.view?.triggerCommand(e);
  }
  //#endregion

  // #region API get/post
  private documentUrlLoaded: string;
  private getDocument() {
    if (this.view?.document?.length > 0) {
      this.markdownParse(this.view.document);
    }
    else if (this._documentUrl?.length > 0) {
      if (this.documentUrlLoaded != this._documentUrl) {
        this.documentUrlLoaded = this._documentUrl;
        this.get(this.documentUrlLoaded).then(content => {
          this.markdownParse(content);
        }).catch(e => this.view.routeOptions.context.ui.errorApi(e, `Hent '${this.view?.documentFileName}`));
      }
    }
  }

  private async get(url: string): Promise<string> {
    return firstValueFrom(this.httpClient.get(url, { responseType: 'text' }));
  }
  // #endregion

  //#region Markdown editor 
  protected allowEdit: boolean = false;

  private divEditEle: HTMLDivElement = null;
  @ViewChild('markdownEditDiv') set markDownDivEditSet(e: ElementRef) {
    const ele = e?.nativeElement;
    if (ele != null && ele != this.divEditEle) {
      this.documentEdit(ele);
    }
    this.divEditEle = ele;
  }

  private edit: CerMarkdownEdit;
  private documentEdit(parent: HTMLDivElement) {
    this.edit = new CerMarkdownEdit();
    this.edit.editDocument(parent, this.markdown);
  }

  private documentEditClose() {
    this.edit.destroy();
    this.edit = null;
    this.editSet(false);
  }

  private async documentEditSaveAndClose() {
    const md = this.edit.getMarkdown();
    if (await this.documentSave(md)) {
      this.markdownParse(md);
      this.documentEditClose();
    }
  }

  private async documentSave(markdown: string): Promise<boolean> {
    const url = this.documentUrlUpdate;
    const fileName = this.view?.documentFileName;
    if (url == null || url.length == 0) {
      this.ui.error("Addresse til gemme mangler. Kan ikke gemme.");
      return false;
    }
    if (fileName == null || fileName.length == 0) {
      this.ui.error("Filnavn mangler. Kan ikke gemme.");
      return false;
    }
    try {
      const encoder = new TextEncoder();
      const byteArray: Uint8Array = encoder.encode(markdown);
      const file = new File([byteArray], this.view?.documentFileName, { type: 'text/markdown' })
      const formData = new FormData();
      formData.append("markdown", file);
      const progress = new CerLinkedAttachmentUploadProgress(this.ui);
      await CerLinkedAttachmentUploader.uploadFormData(formData, url, progress);
      return true;
    }
    catch (error) {
      this.ui.errorApi(error);
      return false;
    }
  }
  //#endregion 

  //#region Pdf
  private downloadAsPdf() {
    CerMarkdownPdf.downloadPdf(this.markdownViewDivEle, this.view?.title ?? "Dokumentation", this.view?.documentFileName ?? "document")
  }
  //#endregion Pdf

}